import type { JSX } from 'react';
import { styled } from 'styled-components';
import { forwardRef, useState } from 'react';
import { isFunction } from '@wistia/type-guards';
import type { InputProps } from '../Input/Input';
import { Input } from '../Input/Input';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';

const StyledIconButton = styled(IconButton)`
  /* override size for icon button since prop gets changed by Input */
  height: var(--icon-button-size-sm);
  width: var(--icon-button-size-sm);

  /* override input padding variable since it is used for Input Icon positioning */
  --wui-input-horizontal-padding: 4px;
`;

export type InputPasswordProps = Omit<
  InputProps,
  'leftIcon' | 'monospace' | 'rightIcon' | 'type'
> & {
  /**
   * Set the disabled state of the input
   */
  disabled?: boolean;
  /**
   * Callback function that is called when the visibility state changes
   */
  onVisibilityToggle?: (isVisible: boolean) => void;
};

/**
 * A password input component with a toggle button to show or hide the password text.
 */
export const InputPassword = forwardRef<HTMLInputElement, InputPasswordProps>(
  ({ onVisibilityToggle, disabled = false, ...props }, ref): JSX.Element => {
    const [isVisible, setIsVisible] = useState(false);

    const handleClick = () => {
      const newVisibility = !isVisible;
      setIsVisible(newVisibility);

      if (isFunction(onVisibilityToggle)) {
        onVisibilityToggle(newVisibility);
      }
    };

    return (
      <Input
        {...props}
        ref={ref}
        disabled={disabled}
        rightIcon={
          <StyledIconButton
            disabled={disabled}
            label={isVisible ? 'Hide password' : 'Show password'}
            onClick={handleClick}
            tabIndex={disabled ? -1 : 0}
            variant="ghost"
          >
            <Icon type={isVisible ? 'preview' : 'hide'} />
          </StyledIconButton>
        }
        type={isVisible ? 'text' : 'password'}
      />
    );
  },
);

InputPassword.displayName = 'InputPassword';
